/**
 * dashboard/quizzes/index.tsx — Tutor quiz list (Phase G.1).
 *
 * Two tabs:
 *   - Library: approved library quizzes (read-only). Tutors browse
 *     to find quizzes to activate for their class. Each item links
 *     to the read-only preview page.
 *   - Quiz Saya: the tutor's own custom quizzes (source = 'custom'
 *     or 'library_pending'). Each row links to edit + submit-to-library.
 *
 * v0.1.4:
 *   - Terminologi user-facing: "quiz" (bukan "kuis"), "Library"
 *     (bukan "Pustaka").
 *   - Filter tingkat diganti level pills L1-L3 (Fondasi / Berkembang /
 *     Mandiri) bergaya worksheet page — tidak lagi filter "Kelas 1-6".
 *     Data tetap sd_grade di belakang; level dihitung client-side
 *     via lib/leveling.ts, jadi daftar library cukup di-fetch sekali.
 *   - Item library sekarang clickable → preview read-only.
 *
 * Data sources:
 *   - GET /api/quiz/library
 *   - GET /api/quiz/mine
 */

import { type ReactNode, useState } from 'react';
import { createFileRoute, Link, redirect } from '@tanstack/react-router';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { PageHeader } from '../../../components/ui/PageHeader.js';
import { StatePanel } from '../../../components/ui/StatePanel.js';
import { freshSessionQuery } from '../../../lib/api/queries.js';
import {
  LEARNING_LEVELS,
  LEARNING_LEVEL_DOTS,
  LEARNING_LEVEL_LABEL,
  toLearningLevel,
  toLearningLevelEnum,
  type LearningLevel,
} from '../../../lib/leveling.js';

export const Route = createFileRoute('/dashboard/quizzes/')({
  beforeLoad: async ({ context, location }) => {
    const session = await context.queryClient.fetchQuery(freshSessionQuery);
    const user = session?.ok ? (session.data?.user ?? null) : null;
    if (!user) {
      throw redirect({ to: '/login', search: { error: undefined, redirect: location.href } });
    }
    if (user.role !== 'tutor' || !user.isApprovedTutor) {
      throw redirect({ to: '/' });
    }
    return null;
  },
  component: TutorQuizzesPage,
});

interface QuizRow {
  id: string;
  title: string;
  description: string | null;
  source: 'library' | 'custom' | 'library_pending';
  sd_grade: number;
  is_active: number;
  reject_reason: string | null;
  updated_at: string;
}

type Tab = 'library' | 'mine';

function levelLabel(sdGrade: number): string {
  const lv = toLearningLevelEnum(sdGrade);
  return lv ? `${lv} ${LEARNING_LEVEL_LABEL[lv]}` : '—';
}

function TutorQuizzesPage(): ReactNode {
  const [tab, setTab] = useState<Tab>('library');
  const [level, setLevel] = useState<LearningLevel | null>(null);
  const queryClient = useQueryClient();

  const libraryQuery = useQuery({
    queryKey: ['quiz', 'library'],
    queryFn: async () => {
      const r = await fetch('/api/quiz/library', { credentials: 'include' });
      const j = (await r.json()) as { ok: boolean; data?: { quizzes: QuizRow[] }; error?: { message: string } };
      if (!j.ok || !j.data) throw new Error(j.error?.message ?? 'Gagal memuat library');
      return j.data.quizzes;
    },
  });

  const mineQuery = useQuery({
    queryKey: ['quiz', 'mine'],
    queryFn: async () => {
      const r = await fetch('/api/quiz/mine', { credentials: 'include' });
      const j = (await r.json()) as { ok: boolean; data?: { quizzes: QuizRow[] }; error?: { message: string } };
      if (!j.ok || !j.data) throw new Error(j.error?.message ?? 'Gagal memuat quiz saya');
      return j.data.quizzes;
    },
    enabled: tab === 'mine',
  });

  const allLibrary = libraryQuery.data ?? [];
  const filteredLibrary = level
    ? allLibrary.filter((q) => toLearningLevelEnum(q.sd_grade) === level)
    : allLibrary;

  const submitToLibrary = async (quizId: string): Promise<void> => {
    const r = await fetch(`/api/quiz/${encodeURIComponent(quizId)}/submit-to-library`, {
      method: 'POST',
      credentials: 'include',
    });
    const j = (await r.json()) as { ok: boolean; error?: { message: string } };
    if (j.ok) {
      await queryClient.invalidateQueries({ queryKey: ['quiz', 'mine'] });
    } else {
      alert(j.error?.message ?? 'Gagal submit ke library');
    }
  };

  return (
    <main className="page-shell">
      <PageHeader
        eyebrow="Dasbor tutor"
        title="Quiz"
        description="Pilih quiz dari library, lalu aktifkan dari tab Quiz di halaman kelas untuk memulai sesi live. Kamu juga bisa buat quiz kustom sendiri."
        action={
          <Link to="/dashboard/quizzes/new" className="button button-primary" data-testid="quiz-new">
            + Buat Quiz Baru
          </Link>
        }
      />

      <div className="filter-cluster" role="tablist" aria-label="Tab quiz tutor">
        <button
          type="button"
          role="tab"
          aria-selected={tab === 'library'}
          className={`filter-segment${tab === 'library' ? ' is-active' : ''}`}
          onClick={() => setTab('library')}
          data-testid="tab-library"
        >
          Library
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={tab === 'mine'}
          className={`filter-segment${tab === 'mine' ? ' is-active' : ''}`}
          onClick={() => setTab('mine')}
          data-testid="tab-mine"
        >
          Quiz Saya
        </button>
      </div>

      {tab === 'library' ? (
        <section className="section" aria-labelledby="library-section">
          <div className="filter-cluster" role="group" aria-label="Level">
            <span className="filter-label">Level</span>
            <div className="filter-pills" data-testid="filter-level-group">
              {LEARNING_LEVELS.map((lv) => {
                const active = level === lv;
                const count = allLibrary.filter(
                  (q) => toLearningLevelEnum(q.sd_grade) === lv,
                ).length;
                return (
                  <button
                    key={lv}
                    type="button"
                    className={active ? 'filter-pill is-active' : 'filter-pill'}
                    aria-pressed={active}
                    onClick={() => setLevel(active ? null : lv)}
                    data-testid={`level-pill-${lv}`}
                  >
                    <span className="filter-pill-dots" aria-hidden="true">
                      {Array.from({ length: 3 }, (_, i) => (
                        <span
                          key={i}
                          className={
                            i < LEARNING_LEVEL_DOTS[lv]
                              ? 'filter-pill-dot is-on'
                              : 'filter-pill-dot'
                          }
                        />
                      ))}
                    </span>
                    <span className="filter-pill-label">
                      {lv} {LEARNING_LEVEL_LABEL[lv]}
                    </span>
                    <span className="filter-pill-count">{count}</span>
                  </button>
                );
              })}
            </div>
          </div>

          {libraryQuery.isLoading ? (
            <StatePanel variant="empty" title="Memuat library…" />
          ) : libraryQuery.isError ? (
            <StatePanel
              variant="error"
              title="Gagal memuat library"
              description={(libraryQuery.error as Error).message}
            />
          ) : filteredLibrary.length === 0 ? (
            <StatePanel
              variant="empty"
              title="Library kosong"
              description={
                level
                  ? `Tidak ada quiz library untuk level ${level} ${LEARNING_LEVEL_LABEL[level]}.`
                  : 'Belum ada quiz di library.'
              }
            />
          ) : (
            <ul className="quiz-list" data-testid="library-list">
              {filteredLibrary.map((q) => (
                <li key={q.id} className="card quiz-list-item">
                  <div className="quiz-list-meta">
                    <span className="card-meta">{levelLabel(q.sd_grade)}</span>
                  </div>
                  <h3 className="card-title">
                    <Link
                      to="/dashboard/quizzes/$id/preview"
                      params={{ id: q.id }}
                      data-testid="library-item"
                    >
                      {q.title}
                    </Link>
                  </h3>
                  {q.description ? <p className="quiz-list-desc">{q.description}</p> : null}
                </li>
              ))}
            </ul>
          )}
        </section>
      ) : (
        <section className="section" aria-labelledby="mine-section">
          {mineQuery.isLoading ? (
            <StatePanel variant="empty" title="Memuat quiz saya…" />
          ) : mineQuery.isError ? (
            <StatePanel
              variant="error"
              title="Gagal memuat quiz"
              description={(mineQuery.error as Error).message}
            />
          ) : !mineQuery.data || mineQuery.data.length === 0 ? (
            <StatePanel
              variant="empty"
              title="Belum ada quiz"
              description="Buat quiz kustom untuk memulai. Quiz kustom tidak masuk library sampai kamu submit untuk review admin."
              action={
                <Link to="/dashboard/quizzes/new" className="button button-primary">
                  Buat Quiz Pertama
                </Link>
              }
            />
          ) : (
            <ul className="quiz-list" data-testid="mine-list">
              {mineQuery.data.map((q) => (
                <li key={q.id} className="card quiz-list-item">
                  <div className="quiz-list-meta">
                    <span className="card-meta">{levelLabel(q.sd_grade)}</span>
                    <span className="card-meta">
                      {q.source === 'library_pending' ? 'Menunggu review' : 'Kustom'}
                    </span>
                  </div>
                  <h3 className="card-title">
                    <Link
                      to="/dashboard/quizzes/$id"
                      params={{ id: q.id }}
                      data-testid="mine-item"
                    >
                      {q.title}
                    </Link>
                  </h3>
                  {q.description ? <p className="quiz-list-desc">{q.description}</p> : null}
                  {q.reject_reason ? (
                    <div className="form-message is-error" role="status" data-testid="reject-reason">
                      Ditolak admin: {q.reject_reason}
                    </div>
                  ) : null}
                  <div className="cluster">
                    <Link
                      to="/dashboard/quizzes/$id/preview"
                      params={{ id: q.id }}
                      className="button button-secondary button-sm"
                      data-testid="mine-preview"
                    >
                      Preview
                    </Link>
                    {q.source === 'custom' && !q.reject_reason ? (
                      <button
                        type="button"
                        className="button button-primary button-sm"
                        onClick={() => submitToLibrary(q.id)}
                        data-testid="mine-submit"
                      >
                        Submit ke Library
                      </button>
                    ) : null}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </section>
      )}
    </main>
  );
}
